import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  FormControlLabel,
  Checkbox,
  IconButton,
  MobileStepper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import NoteAltRounded from "@mui/icons-material/NoteAltRounded";
import ForumRounded from "@mui/icons-material/ForumRounded";
import HistoryRounded from "@mui/icons-material/HistoryRounded";
import { db } from "services/firebase";
import HelpVideo from "components/common/HelpVideo";
import providerVideo from "assets/help/provider-walkthrough.mp4";
import providerCaptions from "assets/help/provider-walkthrough.vtt";
import providerPoster from "assets/help/provider-walkthrough-poster.jpg";

const ToolCard = ({ icon, title, description }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      gap: 2,
      p: 2,
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 2,
      textAlign: "left",
    }}
  >
    {icon}
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Box>
  </Box>
);

const PROVIDER_TOOLS = [
  {
    icon: <NoteAltRounded sx={{ fontSize: 40, flexShrink: 0 }} color="primary" />,
    title: "ChartMind",
    description:
      "Record a patient encounter and let Medical SuperIntelligence suggest diagnoses, build a treatment plan, generate discharge instructions, and produce a finalized clinical note.",
  },
  {
    icon: <ForumRounded sx={{ fontSize: 40, flexShrink: 0 }} color="primary" />,
    title: "PeerView Case Consultation",
    description:
      "Ask follow-up questions about your active ChartMind encounter to narrow a differential, explore alternatives, or get more clinical context.",
  },
  {
    icon: <HistoryRounded sx={{ fontSize: 40, flexShrink: 0 }} color="primary" />,
    title: "My Charts",
    description:
      "Open saved encounters when you need to review a prior ChartMind session.",
  },
];

const ProfessionalOnboardingDialog = ({ open, onClose, userId, userData }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  // Shorter beta disclaimer when the account has betaAccess (Kijabe / sandbox).
  const isBeta = Boolean(userData?.betaAccess);
  const tools = PROVIDER_TOOLS;

  const handleNext = () => setActiveStep((s) => s + 1);
  const handleBack = () => setActiveStep((s) => s - 1);

  const handleClose = () => {
    onClose();

    if (dontShowAgain && userId) {
      db.collection("users")
        .doc(userId)
        .update({ hideOnboardingDialog: true })
        .catch((err) => console.error("Error saving onboarding preference:", err));
    }
  };

  // The disclaimer is a mandatory gate (must acknowledge before continuing); the
  // three content pages — welcome, walkthrough, features — follow it.
  const disclaimerStep = {
    hideNavigation: true,
    content: (
      <Box sx={{ textAlign: "center", py: 2 }}>
        <MedicalServicesIcon color="primary" sx={{ fontSize: 64, mb: 3 }} />
        <Typography variant="h5" sx={{ mb: 3 }}>
          Important Notice for Medical Professionals
        </Typography>
        <Typography variant="body1" paragraph sx={{ textAlign: "center" }}>
          The materials on this site are provided for general medical education
          purposes only and should not be applied rigidly or universally in any
          clinical scenario. Decisions regarding patient care must remain the
          professional responsibility of the individual medical practitioner, who
          must use clinical judgment based on the unique circumstances of each
          case.
        </Typography>
        {isBeta ? (
          <Typography variant="body1" paragraph sx={{ textAlign: "center" }}>
            My HealthDesk and its representatives assume no legal, financial, or
            medical liability for decisions made using this platform. By accessing
            this site, you agree to the full My HealthDesk Terms of Use.
          </Typography>
        ) : (
          <>
            <Typography variant="body1" paragraph sx={{ textAlign: "center" }}>
              My HealthDesk is intended strictly for use by medical professionals.
              Neither My HealthDesk nor its representatives assume legal,
              financial, or medical liability for decisions made using, or based
              on, the information provided through its platform.
            </Typography>
            <Typography variant="body1" paragraph sx={{ textAlign: "center" }}>
              By accessing and using this site, you agree to these conditions as
              well as the full My HealthDesk Terms of Use.
            </Typography>
          </>
        )}
        <Button
          variant="contained"
          onClick={() => setActiveStep(1)}
          sx={{ mt: 2, px: 4 }}
        >
          I Understand
        </Button>
      </Box>
    ),
  };

  const welcomeStep = {
    content: (
      <Box sx={{ textAlign: "center", py: 2 }}>
        <MedicalServicesIcon color="primary" sx={{ fontSize: 64, mb: 3 }} />
        <Typography variant="h5" sx={{ mb: 3 }}>
          Welcome to My HealthDesk
        </Typography>
        <Typography variant="body1" paragraph>
          My HealthDesk provides clinical tools built on a curated
          medical knowledge base.
        </Typography>
        <Typography variant="body1" paragraph>
          Less time searching. More time with patients.
        </Typography>
      </Box>
    ),
  };

  const videoStep = {
    content: (
      <Box sx={{ textAlign: "center", py: 2 }}>
        <Typography variant="h5" sx={{ mb: 1 }}>
          See it in action
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          A quick tour of your clinical tools on My HealthDesk.
        </Typography>
        <HelpVideo
          src={providerVideo}
          captions={providerCaptions}
          poster={providerPoster}
          caption="Watch a quick tour of My HealthDesk."
        />
      </Box>
    ),
  };

  const featuresStep = {
    content: (
      <Box sx={{ py: 2 }}>
        <Typography variant="h5" sx={{ mb: 3, textAlign: "center" }}>
          Your AI assistant suite
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {tools.map((tool) => (
            <ToolCard key={tool.title} {...tool} />
          ))}
        </Box>
        <Typography
          variant="body2"
          sx={{ mt: 3, fontStyle: "italic", color: "text.secondary", textAlign: "center" }}
        >
          Access any tool from the sidebar to get started.
        </Typography>
      </Box>
    ),
  };

  const steps = [disclaimerStep, welcomeStep, videoStep, featuresStep];

  return (
    <Dialog
      open={open}
      onClose={activeStep > 0 ? handleClose : null}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, maxWidth: 680 } }}
    >
      {activeStep > 0 && (
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
            zIndex: 1,
          }}
        >
          <CloseIcon />
        </IconButton>
      )}

      <DialogContent sx={{ p: 4, pb: 1 }}>
        {steps[activeStep].content}

        {!steps[activeStep].hideNavigation && (
          <MobileStepper
            variant="dots"
            steps={steps.length - 1}
            position="static"
            activeStep={activeStep - 1}
            sx={{ background: "transparent", justifyContent: "center", mt: 2 }}
            nextButton={null}
            backButton={null}
          />
        )}
      </DialogContent>

      {!steps[activeStep].hideNavigation && (
        <DialogActions sx={{ justifyContent: "space-between", p: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
              />
            }
            label="Don't show this again"
          />
          <Box>
            {activeStep > 1 && (
              <Button onClick={handleBack} sx={{ mr: 1 }}>
                Back
              </Button>
            )}
            {activeStep === steps.length - 1 ? (
              <Button variant="contained" onClick={handleClose}>
                Get Started
              </Button>
            ) : (
              <Button variant="contained" onClick={handleNext}>
                Next
              </Button>
            )}
          </Box>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ProfessionalOnboardingDialog;
